/**
 * PageSideAura — subtle ambient side aura, scoped to a single content
 * region (not the whole viewport).
 *
 * Owner feedback (May 2026):
 *   "Add them a little bit more often — can't see them when you first
 *    open the page. They need to be on the right and the left of the
 *    public corporate facts that's in the center, all the way going up
 *    and down the page on the right and left-hand side. Just not going
 *    over the content, but not going over the top headers and the bottom
 *    headers."
 *
 * Behaviour:
 *   • `position: absolute` (NOT fixed). Sits inside a parent which must
 *     declare `position: relative`. Only covers the bounds of that parent.
 *   • Five soft radial blobs per side, evenly distributed top-to-bottom
 *     so the side panel feels continuous rather than empty between
 *     waypoints. Alternating colors per band (top → bottom: a, b, a, b, a).
 *   • Higher base opacity (0.85) + stronger gradient stops (color@40%)
 *     so the aura reads immediately on page load — previously it was
 *     too faint to notice.
 *   • Whole composition drifts very slowly (22s alternate) so the eye
 *     picks up motion peripherally without any jarring change.
 *   • Hidden below `md` (768px). `aria-hidden`, `pointer-events: none`.
 */

/**
 * @param {{
 *   accent?: string,    // primary band color
 *   accent2?: string,   // secondary band color
 *   opacity?: number,   // 0–1, default 0.85
 *   testId?: string,
 * }} props
 */
export default function PageSideAura({
  accent = "#5B2E83",
  accent2 = "#0E5FB0",
  opacity = 0.85,
  testId = "page-side-aura",
}) {
  return (
    <div
      aria-hidden
      data-testid={testId}
      className="pointer-events-none absolute inset-0 z-10 hidden md:block overflow-hidden"
      style={{ opacity }}
    >
      <Side side="left" colorA={accent} colorB={accent2} />
      <Side side="right" colorA={accent2} colorB={accent} />
    </div>
  );
}

// Five blob waypoints distributed top → bottom. Each carries a vertical
// offset so the inside (toward content) reads as a soft column rather
// than a single big blur, and an outside offset so the bigger blobs
// "bleed off" the page edge.
const BLOB_TIMINGS = [
  { top: "4%",  size: 280, insetOut: "-40%", insetIn: "0%",   sizeIn: 160 },
  { top: "23%", size: 240, insetOut: "-25%", insetIn: "15%",  sizeIn: 140 },
  { top: "44%", size: 300, insetOut: "-35%", insetIn: "-5%",  sizeIn: 180 },
  { top: "65%", size: 240, insetOut: "-30%", insetIn: "10%",  sizeIn: 140 },
  { top: "84%", size: 280, insetOut: "-40%", insetIn: "0%",   sizeIn: 160 },
];

function Side({ side, colorA, colorB }) {
  const isLeft = side === "left";

  // Mask the inner edge so the aura fades cleanly into the reading column.
  const innerFade = isLeft
    ? "linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)"
    : "linear-gradient(270deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)";

  return (
    <div
      className={[
        "absolute top-0 h-full overflow-hidden",
        isLeft ? "left-0" : "right-0",
        // Wide enough for substantial blobs without crowding the reading column.
        "w-[16vw] md:w-[200px] lg:w-[240px] xl:w-[280px]",
      ].join(" ")}
      style={{
        maskImage: innerFade,
        WebkitMaskImage: innerFade,
      }}
    >
      {/* Whole panel drifts very slowly so motion is felt but never jarring. */}
      <div className="absolute inset-0 aura-drift slow">
        {BLOB_TIMINGS.map((b, i) => {
          // Alternate band color per waypoint.
          const color = i % 2 === 0 ? colorA : colorB;
          return (
            <span key={i}>
              {/* OUTER blob — biggest, sits "off" the page edge. */}
              <span
                className="absolute"
                style={{
                  top: b.top,
                  left: isLeft ? b.insetOut : "auto",
                  right: isLeft ? "auto" : b.insetOut,
                  width: b.size,
                  height: b.size,
                  background: `radial-gradient(circle, ${color}66 0%, ${color}28 38%, transparent 72%)`,
                  filter: "blur(2px)",
                }}
              />
              {/* INNER companion — smaller, slightly inset toward the
                  reading column, slightly offset vertically so the pair
                  reads as a moving cluster rather than a single disc. */}
              <span
                className="absolute"
                style={{
                  top: `calc(${b.top} + 90px)`,
                  left: isLeft ? b.insetIn : "auto",
                  right: isLeft ? "auto" : b.insetIn,
                  width: b.sizeIn,
                  height: b.sizeIn,
                  background: `radial-gradient(circle, ${color}55 0%, ${color}1F 40%, transparent 72%)`,
                  filter: "blur(2px)",
                }}
              />
            </span>
          );
        })}
      </div>
    </div>
  );
}
